import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api';

const formatRelative = (isoStr) => {
  if (!isoStr) return '';
  const date = new Date(isoStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const formatSessionDate = (isoStr) => {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString([], {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const StudentMessages = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/notifications');
      if (!res.ok) { setError('Failed to load notifications'); return; }
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch {
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await apiFetch('/api/notifications/read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ all: true }),
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      // best effort
    }
  };

  const markOneRead = async (id) => {
    try {
      await apiFetch('/api/notifications/read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [id] }),
      });
      setNotifications((prev) =>
        prev.map((n) => n.id === id ? { ...n, is_read: true } : n)
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      // best effort
    }
  };

  return (
    <div className="sessions-page" style={{ overflow: 'auto' }}>
      <div className="sessions-header sessions-header--expanded">
        <div className="sessions-header__content">
          <h1 style={{ margin: 0 }}>
            Notifications
            {unreadCount > 0 && (
              <span style={{
                marginLeft: '0.6rem', background: '#2563eb', color: '#fff',
                borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700,
                padding: '0.15rem 0.55rem', verticalAlign: 'middle',
              }}>
                {unreadCount}
              </span>
            )}
          </h1>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              style={{
                background: 'none', border: '1px solid #cbd5e1', borderRadius: '6px',
                padding: '0.35rem 0.8rem', cursor: 'pointer', fontSize: '0.8rem',
                color: '#475569', marginLeft: 'auto',
              }}
            >
              Mark all read
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: '1.25rem 1rem 2rem', maxWidth: '680px' }}>
        {error && (
          <p style={{ color: '#dc2626', fontSize: '0.875rem', marginBottom: '1rem' }}>{error}</p>
        )}
        {loading && notifications.length === 0 && (
          <p style={{ color: '#64748b' }}>Loading…</p>
        )}
        {!loading && notifications.length === 0 && !error && (
          <section className="detail-empty">
            <div className="empty-card">
              <div className="title">No notifications yet</div>
              <div className="subtitle">Session alerts from your tutor will appear here.</div>
            </div>
          </section>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {notifications.map((n) => {
            const sessionDate = n.metadata?.scheduled_at
              ? formatSessionDate(n.metadata.scheduled_at)
              : null;
            const duration = n.metadata?.duration_minutes;

            return (
              <div
                key={n.id}
                onClick={() => !n.is_read && markOneRead(n.id)}
                style={{
                  background: 'var(--lrbrain-surface, #fff)',
                  border: '1px solid',
                  borderColor: n.is_read ? '#e2e8f0' : '#bfdbfe',
                  borderLeft: `4px solid ${n.is_read ? '#e2e8f0' : '#2563eb'}`,
                  borderRadius: '8px',
                  padding: '0.9rem 1rem',
                  cursor: n.is_read ? 'default' : 'pointer',
                  transition: 'border-color 0.2s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <strong style={{ fontSize: '0.95rem', color: 'var(--lrbrain-text, #0f172a)' }}>
                    {n.title}
                  </strong>
                  <span style={{ fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {formatRelative(n.created_at)}
                  </span>
                </div>
                {n.body && (
                  <p style={{ margin: '0.3rem 0 0', fontSize: '0.875rem', color: '#475569' }}>
                    {n.body}
                  </p>
                )}
                {sessionDate && (
                  <p style={{
                    margin: '0.4rem 0 0', fontSize: '0.8rem', color: '#2563eb', fontWeight: 600,
                  }}>
                    📅 {sessionDate}{duration ? ` · ${duration} min` : ''}
                  </p>
                )}
                {!n.is_read && (
                  <p style={{ margin: '0.4rem 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
                    Click to mark as read
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StudentMessages;
